/**
 * Recherche globale dans l'en-tête de la console Admin.
 *
 * - Champ de saisie avec debounce 300 ms.
 * - Interroge simultanément `GET /personnel/?search=` et `GET /clients/?search=`.
 * - Dropdown de suggestions : résultats cliquables → navigue vers la fiche.
 * - Gère : champ vide (fermé), chargement (spinner), 0 résultat, erreur.
 * - Fermeture au clic extérieur ou appui sur Echap.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Users, User, Loader2 } from 'lucide-react';

import { listPersonnel } from '@/api/personnel';
import { listClients } from '@/api/clients';
import { nomAffichageClient } from '@/api/types';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

const MIN_CHARS = 2;
const MAX_RESULTS_PER_CATEGORY = 5;

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  href: string;
  category: 'personnel' | 'client';
}

export default function GlobalSearch(): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const enabled = debouncedQuery.length >= MIN_CHARS;

  const personnelQuery = useQuery({
    queryKey: ['search', 'personnel', debouncedQuery],
    queryFn: () => listPersonnel({ search: debouncedQuery, page_size: MAX_RESULTS_PER_CATEGORY }),
    enabled,
    staleTime: 30_000,
  });

  const clientsQuery = useQuery({
    queryKey: ['search', 'clients', debouncedQuery],
    queryFn: () => listClients({ search: debouncedQuery, page_size: MAX_RESULTS_PER_CATEGORY }),
    enabled,
    staleTime: 30_000,
  });

  const isLoading = enabled && (personnelQuery.isFetching || clientsQuery.isFetching);
  const isError = personnelQuery.isError || clientsQuery.isError;

  const results: SearchResult[] = [];
  if (enabled) {
    (personnelQuery.data?.results ?? []).forEach((p) => {
      results.push({
        id: `personnel-${p.id}`,
        label: p.full_name,
        sublabel: p.matricule,
        href: `/personnel/${p.id}`,
        category: 'personnel',
      });
    });
    (clientsQuery.data?.results ?? []).forEach((c) => {
      results.push({
        id: `client-${c.id}`,
        label: nomAffichageClient(c),
        sublabel: c.telephone_principal ?? c.phone,
        href: `/clients/${c.id}`,
        category: 'client',
      });
    });
  }

  const noResults =
    enabled &&
    !isLoading &&
    !isError &&
    personnelQuery.isFetched &&
    clientsQuery.isFetched &&
    results.length === 0;

  const showDropdown = open && query.trim().length >= MIN_CHARS;

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      if (containerRef.current === null) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Fermeture à l'appui sur Echap.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  function handleSelect(href: string): void {
    setOpen(false);
    setQuery('');
    navigate(href);
  }

  return (
    <div ref={containerRef} className="relative hidden sm:block">
      <div className="relative flex items-center">
        {isLoading ? (
          <Loader2
            aria-hidden
            className="pointer-events-none absolute left-3 h-4 w-4 animate-spin text-gold-500"
          />
        ) : (
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 h-4 w-4 text-sub"
          />
        )}
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label="Rechercher un personnel ou un client"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls="global-search-results"
          placeholder="Rechercher…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="h-9 w-52 rounded-xl border border-line bg-pearl pl-9 pr-3 font-sans text-sm text-ink placeholder:text-sub/60 focus:border-gold-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-500/30 focus:w-72 transition-all duration-200 lg:w-64 lg:focus:w-80"
        />
      </div>

      {showDropdown && (
        <div
          id="global-search-results"
          role="listbox"
          aria-label="Résultats de recherche"
          className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-line bg-white shadow-lift"
        >
          {isLoading && (
            <p
              role="status"
              aria-live="polite"
              className="px-4 py-3 font-sans text-xs text-sub"
            >
              Recherche en cours…
            </p>
          )}

          {isError && !isLoading && (
            <p
              role="alert"
              className="px-4 py-3 font-sans text-xs text-danger"
            >
              Recherche indisponible. Réessayez.
            </p>
          )}

          {noResults && (
            <p className="px-4 py-3 font-sans text-xs text-sub">
              Aucun résultat pour «&nbsp;{debouncedQuery}&nbsp;».
            </p>
          )}

          {!isLoading && !isError && results.length > 0 && (
            <>
              {/* Groupe Personnel */}
              {results.filter((r) => r.category === 'personnel').length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 border-b border-line bg-ivory px-4 py-2">
                    <Users aria-hidden className="h-3.5 w-3.5 text-sub" />
                    <span className="font-display text-[11px] font-bold uppercase tracking-wider text-sub">
                      Personnel
                    </span>
                  </div>
                  {results
                    .filter((r) => r.category === 'personnel')
                    .map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => handleSelect(r.href)}
                        className="flex w-full items-center gap-3 border-b border-line px-4 py-2.5 text-left transition hover:bg-pearl focus:bg-pearl focus:outline-none"
                      >
                        <User aria-hidden className="h-4 w-4 shrink-0 text-sub" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-xs font-extrabold text-navy-700">
                            {r.label}
                          </p>
                          {r.sublabel !== undefined && (
                            <p className="truncate font-sans text-[11px] text-sub">
                              {r.sublabel}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              )}

              {/* Groupe Clients */}
              {results.filter((r) => r.category === 'client').length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 border-b border-line bg-ivory px-4 py-2">
                    <Users aria-hidden className="h-3.5 w-3.5 text-sub" />
                    <span className="font-display text-[11px] font-bold uppercase tracking-wider text-sub">
                      Clients
                    </span>
                  </div>
                  {results
                    .filter((r) => r.category === 'client')
                    .map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => handleSelect(r.href)}
                        className="flex w-full items-center gap-3 border-b border-line px-4 py-2.5 text-left transition hover:bg-pearl focus:bg-pearl focus:outline-none last:border-b-0"
                      >
                        <User aria-hidden className="h-4 w-4 shrink-0 text-sub" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-xs font-extrabold text-navy-700">
                            {r.label}
                          </p>
                          {r.sublabel !== undefined && (
                            <p className="truncate font-sans text-[11px] text-sub">
                              {r.sublabel}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
